import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import Image from '@atlaskit/image';
import { CloseButton, ModalTransition } from '@atlaskit/modal-dialog';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Heading from '@atlaskit/heading';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import LiveDocModalGifLight from './assets/LiveDocModalGif-light.gif';
import LiveDocModalGifDark from './assets/LiveDocModalGif-dark.gif';
import { useLiveDocModal } from './useLiveDocModal.ts';
import { usePreloadImage } from './usePreloadImage.ts';

export const LiveDocModal = () => {
	const intl = useIntl();
	const { onCloseModal } = useLiveDocModal();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const preloadingGif = usePreloadImage({ light: LiveDocModalGifLight, dark: LiveDocModalGifDark });
	const closeModal = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'dismiss',
				source: 'modernizeConfluenceLiveDocModal',
			},
		}).fire();
		onCloseModal();
	}, [createAnalyticsEvent, onCloseModal]);
	const { Modal } = useChoreographerAssets();

	return (
		<ModalTransition>
			{preloadingGif ? null : (
				<Modal
					messageType="engagement"
					messageId="modernize-changeboarding.liveDocModal"
					onClose={closeModal}
					onOpenComplete={() => {
						createAnalyticsEvent({
							type: 'sendUIEvent',
							data: {
								action: 'rendered',
								actionSubject: 'modal',
								actionSubjectId: 'modernizeConfluenceLiveDoc',
								source: 'modernizeConfluenceLiveDocChangeboarding',
							},
						}).fire();
					}}
					shouldCloseOnEscapePress={false}
					shouldCloseOnOverlayClick={false}
				>
					<Box xcss={containerStyles}>
						<Box xcss={closeContainerStyles}>
							<CloseButton onClick={closeModal} testId="close-modal-button" />
						</Box>
						<Box>
							<Image src={LiveDocModalGifLight} srcDark={LiveDocModalGifDark} alt="" />
						</Box>
					</Box>
					<Box xcss={textContainerStyles} padding="space.500" paddingBlockEnd="space.400">
						<Box xcss={headingContainerStyles}>
							<Heading size="medium">{intl.formatMessage(messages.heading)}</Heading>
						</Box>
						<Box paddingBlockStart="space.200" as="p" xcss={bodyStyles}>
							{intl.formatMessage(messages.body, {
								strong: (chunks: React.ReactNode[]) => <Text as="strong">{chunks}</Text>,
							})}
						</Box>
						<Box paddingBlockStart="space.400" xcss={ctaContainerStyles}>
							<Button onClick={closeModal} appearance="discovery">
								{intl.formatMessage(messages.cta)}
							</Button>
						</Box>
					</Box>
				</Modal>
			)}
		</ModalTransition>
	);
};

const closeContainerStyles = xcss({
	position: 'absolute',
	insetBlockStart: 'space.200',
	insetInlineEnd: 'space.200',
	backgroundColor: 'color.background.input',
	border: `${token('border.width')} solid ${token('color.border')}`,
	borderRadius: 'radius.small',
});

const containerStyles = xcss({
	position: 'relative',
	borderTopRightRadius: 'radius.small',
	borderTopLeftRadius: 'radius.small',
	overflow: 'hidden',
});

const textContainerStyles = xcss({
	backgroundColor: 'utility.elevation.surface.current',
	borderRadius: 'radius.small',
});

const headingContainerStyles = xcss({
	textAlign: 'center',
});

const ctaContainerStyles = xcss({
	textAlign: 'center',
});

const bodyStyles = xcss({
	margin: '0',
	textAlign: 'center',
});

const messages = defineMessages({
	heading: {
		id: 'modernize-changeboarding.live-doc-modal.heading',
		defaultMessage: 'Make instant edits with Live Docs',
		description: 'Title of modal introducing Live Docs functionality',
	},
	body: {
		id: 'modernize-changeboarding.live-doc-modal.body',
		defaultMessage:
			'Welcome to the new live docs experience, where you can work and collaborate in <strong>real time</strong> without ever having to publish.',
		description: 'Body of modal introducing Live Docs functionality',
	},
	cta: {
		id: 'modernize-changeboarding.live-doc-modal.cta',
		defaultMessage: 'Let’s go',
		description: 'CTA to view the Live Docs Tutorial',
	},
});
